/** @jsxImportSource theme-ui */
import React from 'react';
import { Box, Text } from '@infotrack/zenith-ui';
import { Controller, Control, FieldValues, Path } from 'react-hook-form';
import { usePeriods, type PeriodOption } from '@/features/topfive/hooks/usePeriods';

interface PeriodSelectorProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  error?: string;
  currentOnly?: boolean; // Filter to show only current periods
}

/**
 * Reusable period selector component with Controller integration.
 * Fetches periods from API and displays them in a dropdown.
 * 
 * @param currentOnly - If true, filters to show only current periods (not previous)
 * 
 * @example
 * ```tsx
 * <PeriodSelector
 *   name="periodId"
 *   control={control}
 *   label="Top5 Period"
 *   placeholder="Select a period..."
 *   error={errors.periodId?.message}
 *   currentOnly={true}
 * />
 * ```
 */
export const PeriodSelector = <T extends FieldValues>({
  name,
  control,
  disabled = false,
  label = 'Top5 Period',
  placeholder = 'Select a period...',
  error,
  currentOnly = false,
}: PeriodSelectorProps<T>): React.ReactElement => {
  const { data: periods, isLoading: isLoadingPeriods, error: periodsError } = usePeriods();

  // Filter to current periods only if requested
  const filteredPeriods = currentOnly
    ? periods?.filter((p: PeriodOption) => p.period === 'Current')
    : periods;

  const errorTextStyles = {
    color: 'error',
    marginTop: 1,
  };

  const labelStyles = {
    marginBottom: 2,
    fontWeight: 'medium',
  };

  return (
    <Box>
      {label && (
        <Box sx={labelStyles}>
          <label htmlFor={name}>
            <Text variant="medium">{label}</Text>
          </label>
        </Box>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <>
            {isLoadingPeriods ? (
              <Box sx={{ padding: '0.75rem 1rem', border: '1px solid #d1d5db', borderRadius: '8px' }}>
                <Text variant="small">Loading periods...</Text>
              </Box>
            ) : periodsError ? (
              <Box sx={errorTextStyles}>
                <Text variant="caption">Failed to load periods. Please refresh the page.</Text>
              </Box>
            ) : (
              <select
                id={name}
                {...field}
                disabled={disabled || isLoadingPeriods}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  backgroundColor: 'white',
                  cursor: disabled || isLoadingPeriods ? 'not-allowed' : 'pointer',
                  opacity: disabled || isLoadingPeriods ? 0.6 : 1,
                }}
              >
                <option value="">{placeholder}</option>
                {filteredPeriods?.map((period: PeriodOption) => (
                  <option
                    key={`${period.cadenceType}-${period.period}`}
                    value={JSON.stringify(period)}
                  >
                    {period.displayName} - {period.description}
                  </option>
                ))}
              </select>
            )}
          </>
        )}
      />
      {error && (
        <Box sx={errorTextStyles}>
          <Text variant="caption">{error}</Text>
        </Box>
      )}
    </Box>
  );
};
